import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { DocumentCardActions } from "./card/DocumentCardActions";
import { DocumentCardPreview } from "./card/DocumentCardPreview";
import { DocumentViewer } from "./DocumentViewer";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { nb } from 'date-fns/locale';
import { Badge } from "@/components/ui/badge";
import { DocumentCategorySelect } from "./card/DocumentCategorySelect";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Document {
  id: string;
  title: string;
  content: string | null;
  document_type: string;
  created_at: string;
  updated_at: string;
  case_id: string | null;
  user_id: string;
  is_favorite?: boolean;
  category?: string;
  version?: number;
  shared_with?: string[];
  template_id?: string;
}

interface DocumentCardProps {
  document: Document;
  onView: (doc: Document) => void;
  onDownload: (doc: Document) => void;
  onDelete: (id: string) => void;
  onShare?: (doc: Document) => void;
  onVersionHistory?: (doc: Document) => void;
}

export function DocumentCard({ 
  document, 
  onView, 
  onDownload, 
  onDelete,
  onShare,
  onVersionHistory 
}: DocumentCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(document.is_favorite);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(document.category || 'ukategorisert');

  const categoryLabels: { [key: string]: string } = {
    kontrakter: "Kontrakt",
    avtaler: "Avtale",
    korrespondanse: "Korrespondanse",
    rettslige_dokumenter: "Rettslig dokument",
    personvern: "Personvern",
    annet: "Annet",
    ukategorisert: "Ukategorisert"
  };

  const documentTypeLabels: { [key: string]: string } = {
    custom: "Egendefinert",
    template: "Mal",
    generated: "Generert",
    workflow_generated: "Arbeidsflyt",
    imported: "Importert"
  };

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const handleView = () => {
    setIsViewerOpen(true);
    onView(document);
  };

  const handleCategoryChange = async (newCategory: string) => {
    try {
      const { error } = await supabase
        .from('documents')
        .update({ category: newCategory })
        .eq('id', document.id)
        .eq('user_id', document.user_id);

      if (error) throw error;

      setCurrentCategory(newCategory);
      toast.success("Kategori oppdatert");
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error("Kunne ikke oppdatere kategori");
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="h-full w-full"
      >
        <Card 
          className="group relative p-4 sm:p-6 transition-all duration-200 hover:shadow-lg bg-white/50 backdrop-blur-sm border border-amber-100/50 hover:border-amber-200 w-full h-full flex flex-col cursor-pointer"
          onClick={handleView}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex flex-col space-y-2">
            <h3 className="text-lg font-semibold text-amber-900 line-clamp-2">
              {document.title}
            </h3>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                {documentTypeLabels[document.document_type] || document.document_type}
              </Badge>
              <Badge variant="outline" className="bg-amber-50/50">
                {categoryLabels[currentCategory] || currentCategory}
              </Badge>
              {document.version && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Versjon {document.version}
                </Badge>
              )}
              {document.shared_with && document.shared_with.length > 0 && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Delt med {document.shared_with.length}
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-4" onClick={(e) => e.stopPropagation()}>
            <DocumentCategorySelect
              currentCategory={currentCategory}
              onCategoryChange={handleCategoryChange}
              document={document}
            />
          </div>

          <DocumentCardActions
            document={document}
            isFavorite={isFavorite}
            onView={handleView}
            onDownload={onDownload}
            onDelete={onDelete}
            onShare={onShare}
            onVersionHistory={onVersionHistory}
            toggleFavorite={toggleFavorite}
          />

          <Separator className="my-4 bg-amber-100/50" />
          
          <AnimatePresence>
            {isHovered && document.content && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden rounded-md bg-amber-50/30 p-3"
              >
                <DocumentCardPreview content={document.content} />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-4 text-xs text-muted-foreground">
            Opprettet {format(new Date(document.created_at), 'dd.MM.yyyy', { locale: nb })}
          </div>
        </Card>
      </motion.div>

      <DocumentViewer
        document={document}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        onDownload={onDownload}
        onShare={onShare}
        onVersionHistory={onVersionHistory}
      />
    </>
  );
}